import { BaseGenerator } from './BaseGenerator.js';
import { ZONE } from './ZoneTypes.js';
import { MapPipeline } from './MapPipeline.js';

/**
 * Apartment complex generator.
 * Produces multiple buildings around a central parking lot, each with
 * units containing living, bedroom, kitchen, bathroom rooms connected
 * by a shared hallway.
 *
 * Options:
 * - units: 4-8 (default 4)
 * - quality: 'draft' | 'standard' | 'high'
 *
 * Ported from OMN's generateApartmentComplex.
 */
export class ApartmentComplexGenerator extends BaseGenerator {
  generate(content) {
    const quality = this.options.quality || 'draft';
    const tiles = content.tiles || ApartmentComplexGenerator.DEFAULT_TILES;
    const opts = { ...ApartmentComplexGenerator.DEFAULT_OPTIONS, ...this.options };
    const units = Math.max(4, Math.min(8, opts.units || 4));

    const buildingCount = units <= 4 ? 2 : units <= 6 ? 2 : 3;
    const unitsPerBuilding = Math.ceil(units / buildingCount);

    const unitW = 8;
    const unitH = 7;
    const hallwayW = 2;

    const bldgW = unitsPerBuilding * unitW + 2;
    const bldgH = unitH * 2 + hallwayW + 2;

    const centerX = Math.floor(this.width / 2);
    const centerY = Math.floor(this.height / 2) - 3;

    const parkingW = 16;
    const parkingH = 12;
    const parkingLeft = centerX - Math.floor(parkingW / 2);
    const parkingTop = centerY - Math.floor(parkingH / 2);

    const ground = this.createGrid(this.width, this.height, ZONE.GRASS);
    const objects = this.createGrid(this.width, this.height, ZONE.EMPTY);
    const overhead = this.createGrid(this.width, this.height, ZONE.EMPTY);
    const allRooms = [];

    // 1. Grass
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        ground[y][x] = ZONE.GRASS;
      }
    }
    this._addGrassVariation(ground, 5);

    // 2. Parking lot
    this.fillRect(ground, parkingLeft, parkingTop, parkingLeft + parkingW - 1, parkingTop + parkingH - 1, ZONE.ASPHALT);

    // 3. Road along south edge
    const roadTop = this.height - 7;
    this._fillRoad(ground, roadTop, 3);
    this._fillSidewalkRow(ground, roadTop - 1);
    this._fillSidewalkRow(ground, roadTop + 3);

    // Connect parking to road
    const connCol = centerX - 2;
    this.fillRect(ground, connCol, parkingTop + parkingH, connCol + 3, roadTop - 1, ZONE.ASPHALT);

    let boundsLeft = this.width, boundsTop = this.height, boundsRight = 0, boundsBottom = 0;

    const unitTypes = ['living', 'bedroom', 'kitchen', 'bathroom'];

    for (let bi = 0; bi < buildingCount; bi++) {
      let bhl, bht;

      if (bi === 0) {
        bhl = parkingLeft - bldgW - 1;
        bht = centerY - Math.floor(bldgH / 2);
      } else if (bi === 1) {
        bhl = centerX - Math.floor(bldgW / 2);
        bht = parkingTop + parkingH + 1;
      } else {
        bhl = parkingLeft + parkingW + 1;
        bht = centerY - Math.floor(bldgH / 2);
      }

      // Clamp to map
      bhl = Math.max(2, Math.min(bhl, this.width - bldgW - 2));
      bht = Math.max(2, Math.min(bht, this.height - bldgH - 8));

      const bhr = bhl + bldgW;
      const bhb = bht + bldgH;

      boundsLeft = Math.min(boundsLeft, bhl);
      boundsTop = Math.min(boundsTop, bht);
      boundsRight = Math.max(boundsRight, bhr);
      boundsBottom = Math.max(boundsBottom, bhb);

      // Fill building interior
      this.fillRect(ground, bhl, bht, bhr - 1, bhb - 1, ZONE.LIVING);

      // Perimeter walls
      this._placePerimeterWalls(objects, bhl, bht, bhr, bhb);

      // Hallway through center
      const hallwayTop = bht + unitH + 1;
      const hallwayBot = hallwayTop + hallwayW;
      this.fillRect(ground, bhl + 1, hallwayTop, bhr - 2, hallwayBot - 1, ZONE.HALLWAY);

      // Hallway walls
      for (let c = bhl + 1; c < bhr - 1; c++) {
        this.setCell(objects, c, hallwayTop, ZONE.WALL_INT);
        this.setCell(objects, c, hallwayBot, ZONE.WALL_INT);
      }

      // Generate units on top and bottom rows
      for (let ui = 0; ui < unitsPerBuilding; ui++) {
        const ux = bhl + 1 + ui * unitW;
        const topUnitY = bht + 1;
        const botUnitY = hallwayBot + 1;
        const uw = Math.min(unitW, bhr - 1 - ux);
        const uh = unitH - 1;

        if (uw < 4) continue;

        // Vertical wall between units
        if (ui > 0) {
          for (let r = bht + 1; r < hallwayTop; r++) this.setCell(objects, ux, r, ZONE.WALL_INT);
          for (let r = hallwayBot + 1; r < bhb - 1; r++) this.setCell(objects, ux, r, ZONE.WALL_INT);
        }

        // Top unit floor
        const topRoomType = unitTypes[ui % unitTypes.length];
        const topZone = this._roomTypeToZone(topRoomType);
        this.fillRect(ground, ux, topUnitY, ux + uw - 1, hallwayTop - 1, topZone);
        allRooms.push({ x: ux, y: topUnitY, width: uw, height: hallwayTop - topUnitY, type: topRoomType });

        // Door from top unit to hallway
        const topDoorCol = Math.floor(ux + uw / 2);
        if (topDoorCol > bhl && topDoorCol < bhr - 1) {
          this.setCell(objects, topDoorCol, hallwayTop, ZONE.DOORFRAME);
          if (topDoorCol + 1 < bhr - 1) this.setCell(objects, topDoorCol + 1, hallwayTop, ZONE.DOORFRAME);
        }

        // Bottom unit floor
        const botRoomType = unitTypes[(ui + 2) % unitTypes.length];
        const botZone = this._roomTypeToZone(botRoomType);
        this.fillRect(ground, ux, botUnitY, ux + uw - 1, bhb - 2, botZone);
        allRooms.push({ x: ux, y: botUnitY, width: uw, height: bhb - 1 - botUnitY, type: botRoomType });

        // Door from bottom unit to hallway
        const botDoorCol = Math.floor(ux + uw / 2);
        if (botDoorCol > bhl && botDoorCol < bhr - 1) {
          this.setCell(objects, botDoorCol, hallwayBot, ZONE.DOORFRAME);
          if (botDoorCol + 1 < bhr - 1) this.setCell(objects, botDoorCol + 1, hallwayBot, ZONE.DOORFRAME);
        }
      }

      // Hallway room
      allRooms.push({ x: bhl + 1, y: hallwayTop + 1, width: bhr - bhl - 2, height: hallwayW - 1, type: 'hallway' });

      // Exterior doors
      const extDoorCol = Math.floor((bhl + bhr) / 2);
      const extDoorRow = bhb - 1;
      if (extDoorCol > bhl && extDoorCol < bhr - 1) {
        this.setCell(objects, extDoorCol, extDoorRow, ZONE.DOOR);
        this.setCell(ground, extDoorCol, extDoorRow, ZONE.HALLWAY);
      }
      // Back door
      const backCol = Math.floor((bhl + bhr) / 2) + 2;
      if (backCol > bhl && backCol < bhr - 1) {
        this.setCell(objects, backCol, bht, ZONE.DOOR);
        this.setCell(ground, backCol, bht, ZONE.LIVING);
      }

      // Windows
      this._placeExteriorWindows(objects, bhl, bht, bhr, bhb, 4);
    }

    // Parking lot cars
    this.setCell(objects, parkingLeft + 2, parkingTop + 2, ZONE.CRATE);
    this.setCell(objects, parkingLeft + 6, parkingTop + 2, ZONE.CRATE);
    this.setCell(objects, parkingLeft + parkingW - 3, parkingTop + parkingH - 3, ZONE.CRATE);
    this.setCell(objects, parkingLeft + parkingW - 7, parkingTop + parkingH - 3, ZONE.CRATE);

    // Dumpster area
    this.setCell(objects, boundsRight + 2, boundsTop + 1, ZONE.CRATE);
    this.setCell(objects, boundsRight + 2, boundsTop + 3, ZONE.CRATE);

    // Room decorations
    this._decorateRooms(objects, allRooms);

    // Trees and road cars
    this._scatterYardDecorations(objects, boundsLeft, boundsTop, boundsRight, boundsBottom,
      this.rng.nextInt(5, 8), this.rng.nextInt(3, 5));
    this._placeRoadCars(objects, roadTop, boundsLeft, boundsRight, this.rng.nextInt(2, 3));

    if (quality !== 'draft') {
      const pipeline = MapPipeline.createDefault(this.rng, { tileMeta: this.options.tileMeta });
      if (quality !== 'high') pipeline.removePass('decoration');

      const pipelineOptions = {
        tileMapping: this._buildTileMapping(tiles),
        floorTileIds: [ZONE.FLOOR, ZONE.DOOR, ZONE.LIVING, ZONE.BEDROOM,
          ZONE.KITCHEN, ZONE.BATHROOM, ZONE.HALLWAY],
        corridorTileId: ZONE.FLOOR,
        cleanup: { minPatchSize: 0, smoothIterations: 0 },
      };

      const result = pipeline.run({
        ground, objects, overhead,
        zones: ground,
        width: this.width, height: this.height,
        metadata: { rooms: allRooms },
      }, pipelineOptions);

      const finalGround = this._resolveZones(result.ground, tiles);
      const finalObjects = this._resolveObjectZones(result.objects, tiles);
      const finalOverhead = result.overhead.map(row => [...row]);

      if (tiles._tileLayers) {
        this.redistributeByLayer(
          { ground: finalGround, objects: finalObjects, overhead: finalOverhead },
          tiles._tileLayers,
        );
      }

      return this.buildTMJ({
        layers: [
          { name: 'Ground', data: this.flattenGrid(finalGround) },
          { name: 'Objects', data: this.flattenGrid(finalObjects) },
          { name: 'Overhead', data: this.flattenGrid(finalOverhead) },
        ],
        tilesets: content.tilesets || [],
        properties: {
          generator: { type: 'string', value: 'apartment' },
          rooms: { type: 'string', value: JSON.stringify(allRooms) },
        },
      });
    }

    const draftGround = this._resolveZones(ground, tiles);
    const draftObjects = this._resolveObjectZones(objects, tiles);
    const draftOverhead = overhead.map(row => [...row]);

    if (tiles._tileLayers) {
      this.redistributeByLayer(
        { ground: draftGround, objects: draftObjects, overhead: draftOverhead },
        tiles._tileLayers,
      );
    }

    return this.buildTMJ({
      layers: [
        { name: 'Ground', data: this.flattenGrid(draftGround) },
        { name: 'Objects', data: this.flattenGrid(draftObjects) },
        { name: 'Overhead', data: this.flattenGrid(draftOverhead) },
      ],
      tilesets: content.tilesets || [],
      properties: {
        generator: { type: 'string', value: 'apartment' },
        rooms: { type: 'string', value: JSON.stringify(allRooms) },
      },
    });
  }

  // ---- Helpers ----

  _decorateRooms(objects, rooms) {
    for (const room of rooms) {
      if (!room.type || room.width < 3 || room.height < 3) continue;
      if (room.type === 'bedroom') {
        // Bed + desk
        this.setCell(objects, room.x + 1, room.y + 1, ZONE.BED);
        if (room.width > 4) {
          this.setCell(objects, room.x + room.width - 2, room.y + 1, ZONE.DESK);
        }
      } else if (room.type === 'kitchen') {
        // Counter along top wall
        for (let x = room.x + 1; x < room.x + room.width - 1; x++) {
          if (this.rng.chance(0.6)) {
            this.setCell(objects, x, room.y + 1, ZONE.COUNTER);
          }
        }
      } else if (room.type === 'bathroom') {
        // Counter
        this.setCell(objects, room.x + 1, room.y + 1, ZONE.COUNTER);
      } else if (room.type === 'living') {
        // Table + chairs
        const cx = Math.floor(room.x + room.width / 2);
        const cy = Math.floor(room.y + room.height / 2);
        this.setCell(objects, cx, cy, ZONE.TABLE);
        if (cx - 1 >= room.x + 1) this.setCell(objects, cx - 1, cy, ZONE.CHAIR);
        if (cx + 1 <= room.x + room.width - 2) this.setCell(objects, cx + 1, cy, ZONE.CHAIR);
      }
    }
  }

  _roomTypeToZone(roomType) {
    const map = {
      master: ZONE.MASTER, bedroom: ZONE.BEDROOM, bathroom: ZONE.BATHROOM,
      kitchen: ZONE.KITCHEN, living: ZONE.LIVING, dining: ZONE.DINING,
      office: ZONE.OFFICE, storage: ZONE.STORAGE, hallway: ZONE.HALLWAY,
      laundry: ZONE.LAUNDRY, store: ZONE.STORE, backroom: ZONE.BACKROOM,
      restroom: ZONE.RESTROOM,
    };
    return map[roomType] || ZONE.FLOOR;
  }

  _fillRoad(ground, roadTop, roadHeight) {
    for (let r = roadTop; r < roadTop + roadHeight && r < this.height; r++) {
      for (let c = 0; c < this.width; c++) {
        this.setCell(ground, c, r, ZONE.ROAD);
      }
    }
  }

  _fillSidewalkRow(ground, row) {
    if (row < 0 || row >= this.height) return;
    for (let c = 0; c < this.width; c++) {
      this.setCell(ground, c, row, ZONE.SIDEWALK);
    }
  }

  _placePerimeterWalls(objects, hl, ht, hr, hb) {
    for (let c = hl; c < hr; c++) {
      this.setCell(objects, c, ht, ZONE.WALL_EXT);
      this.setCell(objects, c, hb - 1, ZONE.WALL_EXT);
    }
    for (let r = ht; r < hb; r++) {
      this.setCell(objects, hl, r, ZONE.WALL_EXT);
      this.setCell(objects, hr - 1, r, ZONE.WALL_EXT);
    }
  }

  _placeExteriorWindows(objects, hl, ht, hr, hb, interval) {
    for (let c = hl + 2; c < hr - 2; c++) {
      if ((c - hl) % interval === 0) {
        if (this.getCell(objects, c, ht) === ZONE.WALL_EXT) this.setCell(objects, c, ht, ZONE.WINDOW);
        if (this.getCell(objects, c, hb - 1) === ZONE.WALL_EXT) this.setCell(objects, c, hb - 1, ZONE.WINDOW);
      }
    }
    for (let r = ht + 2; r < hb - 2; r++) {
      if ((r - ht) % interval === 0) {
        if (this.getCell(objects, hl, r) === ZONE.WALL_EXT) this.setCell(objects, hl, r, ZONE.WINDOW);
        if (this.getCell(objects, hr - 1, r) === ZONE.WALL_EXT) this.setCell(objects, hr - 1, r, ZONE.WINDOW);
      }
    }
  }

  _scatterYardDecorations(objects, hl, ht, hr, hb, treeCount, bushCount) {
    const margin = 3;
    const placed = new Set();
    const tryPlace = (zone, count) => {
      let n = 0;
      for (let attempt = 0; attempt < count * 10 && n < count; attempt++) {
        const c = this.rng.nextInt(margin, this.width - margin - 1);
        const r = this.rng.nextInt(margin, this.height - margin - 1);
        if (c >= hl - 1 && c < hr + 1 && r >= ht - 1 && r < hb + 1) continue;
        const key = `${c},${r}`;
        if (placed.has(key)) continue;
        placed.add(key);
        this.setCell(objects, c, r, zone);
        n++;
      }
    };
    tryPlace(ZONE.TREE, treeCount);
    tryPlace(ZONE.BUSH, bushCount);
  }

  _placeRoadCars(objects, roadTop, hl, hr, count) {
    for (let i = 0; i < count; i++) {
      const c = this.rng.nextInt(4, this.width - 4);
      if (c >= hl - 1 && c <= hr + 1) continue;
      this.setCell(objects, c, roadTop + 1, ZONE.CRATE);
    }
  }

  _buildTileMapping(tiles) {
    const mapping = {};
    mapping[ZONE.GRASS] = { center: tiles.grass || 1 };
    mapping[ZONE.ROAD] = { center: tiles.road || tiles.asphalt || 1 };
    mapping[ZONE.SIDEWALK] = { center: tiles.sidewalk || 1 };
    mapping[ZONE.ASPHALT] = { center: tiles.asphalt || 1 };
    mapping[ZONE.WALL_EXT] = { center: tiles.wallExt || tiles.wall };
    mapping[ZONE.WALL_INT] = { center: tiles.wallInt || tiles.wall };
    mapping[ZONE.FLOOR] = { center: tiles.floor };
    mapping[ZONE.LIVING] = { center: tiles.floorLiving || tiles.floor };
    mapping[ZONE.BEDROOM] = { center: tiles.floorBedroom || tiles.floor };
    mapping[ZONE.KITCHEN] = { center: tiles.floorKitchen || tiles.floor };
    mapping[ZONE.BATHROOM] = { center: tiles.floorBathroom || tiles.floor };
    mapping[ZONE.HALLWAY] = { center: tiles.floorHallway || tiles.floor };
    mapping[ZONE.DOOR] = { center: tiles.door || tiles.floor };
    mapping[ZONE.DOORFRAME] = { center: tiles.doorframe || tiles.floor };
    return mapping;
  }

  _resolveZones(grid, tiles) {
    const mapping = {
      [ZONE.EMPTY]: 0,
      [ZONE.GRASS]: tiles.grass || 1,
      [ZONE.ROAD]: tiles.road || tiles.asphalt || 1,
      [ZONE.SIDEWALK]: tiles.sidewalk || 1,
      [ZONE.ASPHALT]: tiles.asphalt || 1,
      [ZONE.WALL]: tiles.wall,
      [ZONE.FLOOR]: tiles.floor,
      [ZONE.LIVING]: tiles.floorLiving || tiles.floor,
      [ZONE.BEDROOM]: tiles.floorBedroom || tiles.floor,
      [ZONE.KITCHEN]: tiles.floorKitchen || tiles.floor,
      [ZONE.BATHROOM]: tiles.floorBathroom || tiles.floor,
      [ZONE.HALLWAY]: tiles.floorHallway || tiles.floor,
      [ZONE.DOOR]: tiles.door || tiles.floor,
    };
    return grid.map(row => row.map(z => mapping[z] !== undefined ? mapping[z] : z));
  }

  _resolveObjectZones(grid, tiles) {
    const mapping = {
      [ZONE.EMPTY]: 0,
      [ZONE.WALL_EXT]: tiles.wallExt || tiles.wall,
      [ZONE.WALL_INT]: tiles.wallInt || tiles.wall,
      [ZONE.WINDOW]: tiles.window || 0,
      [ZONE.DOOR]: tiles.door || tiles.floor,
      [ZONE.DOORFRAME]: tiles.doorframe || tiles.floor,
      [ZONE.FENCE]: tiles.fence || 0,
      [ZONE.TREE]: tiles.tree || 0,
      [ZONE.BUSH]: tiles.bush || 0,
      [ZONE.CRATE]: tiles.crate || 0,
      [ZONE.BED]: tiles.bed || 0,
      [ZONE.DESK]: tiles.desk || 0,
      [ZONE.COUNTER]: tiles.counter || 0,
      [ZONE.TABLE]: tiles.table || 0,
      [ZONE.CHAIR]: tiles.chair || 0,
    };
    return grid.map(row => row.map(z => mapping[z] !== undefined ? mapping[z] : z));
  }

  static DEFAULT_TILES = {
    wall: 5,
    wallExt: 5,
    wallInt: 5,
    floor: 6,
    door: 7,
    doorframe: 6,
    grass: 1,
    road: 16,
    asphalt: 16,
    sidewalk: 8,
    window: 5,
    fence: 23,
    tree: 10,
    bush: 11,
    crate: 41,
    bed: 37,
    desk: 44,
    counter: 42,
    table: 38,
    chair: 39,
    floorLiving: 25,
    floorBedroom: 6,
    floorKitchen: 6,
    floorBathroom: 6,
    floorHallway: 6,
  };

  static DEFAULT_OPTIONS = {
    units: 4,
    quality: 'draft',
  };

  static getEmittedZones() {
    return [
      ZONE.GRASS, ZONE.ROAD, ZONE.SIDEWALK, ZONE.ASPHALT, ZONE.WALL_EXT, ZONE.WALL_INT, ZONE.WALL, ZONE.DOOR, ZONE.DOORFRAME, ZONE.WINDOW, ZONE.FENCE, ZONE.FLOOR, ZONE.MASTER, ZONE.BEDROOM, ZONE.BATHROOM, ZONE.KITCHEN, ZONE.LIVING, ZONE.DINING, ZONE.OFFICE, ZONE.STORAGE, ZONE.HALLWAY, ZONE.LAUNDRY, ZONE.STORE, ZONE.BACKROOM, ZONE.RESTROOM, ZONE.TREE, ZONE.BUSH, ZONE.CRATE, ZONE.BED, ZONE.DESK, ZONE.COUNTER, ZONE.TABLE, ZONE.CHAIR,
    ];
  }
}
